/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo, JSXElementConstructor, Key, ReactElement } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";

// Material Dashboard 2 PRO React TS exampless
import Sidenav from "examples/Sidenav";

// Material Dashboard 2 PRO React TS themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 PRO React TS Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// Material Dashboard 2 PRO React TS routes
import routes from "routes";
import StripeContext from "stripeContext";
import { loadStripe } from "@stripe/stripe-js";

// Material Dashboard 2 PRO React TS contexts
import { useMaterialUIController, setMiniSidenav } from "context";

// Images
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";
import PrivateRoute from "components/PrivateRoute";
import { useIntercom } from "components/IntercomChat";
import { analytics } from "lib/segment";
import MobileBanner from "components/mobile-banner/mobile-banner";
import { HeadEnhanceRoute } from "components/HeadEnhanceRoute/HeadEnhanceRoute";
import { Route as RouteType } from "types/Routes";
import { SnackbarProvider } from "notistack";

const stripePromise = loadStripe(
  "pk_test_51Kb1AKFGYJNolA8L6pQRbywhWPykUJUjK01PjE61Gl5UfnCPERlnrMgU9sDq0QM6Mks3eaXczNPyOcjbsjZrVzGv00trGOGjEO"
);

export default function App() {
  const [controller, dispatch] = useMaterialUIController();

  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
    showMobileBanner,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { pathname } = useLocation();

  useIntercom();

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  useEffect(() => {
    analytics.identify();
  }, []);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    (async () => {
      await new Promise((resolve) => setTimeout(resolve, 100));
      /**
       * @description Track page views for horse profiles
       */
      if (/^\/horses\/\d+$/.test(pathname)) {
        analytics.page("Horse profile");
      } else {
        analytics.page();
      }
    })();
  }, [pathname]);

  const getRoutes = (allRoutes: RouteType[]): any =>
    allRoutes.map((route: RouteType) => {
      if (route.collapse) {
        return getRoutes(route.collapse as RouteType[]);
      }

      if (route.route) {
        return route.private ? (
          <Route
            path={route.route}
            element={
              <HeadEnhanceRoute route={route}>
                <PrivateRoute>{route.component as ReactElement}</PrivateRoute>
              </HeadEnhanceRoute>
            }
            key={route.key}
          />
        ) : (
          <Route
            path={route.route}
            element={
              <HeadEnhanceRoute route={route}>{route.component as ReactElement}</HeadEnhanceRoute>
            }
            key={route.key}
          />
        );
      }

      return null;
    });

  return direction === "rtl" ? (
    <StripeContext.Provider value={stripePromise}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        {layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
              brandName="EquiTrace Dashboard"
              routes={routes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
          </>
        )}
        <Routes>
          {getRoutes(routes)}
          <Route path="*" element={<Navigate to="/horses/all-horses" />} />
        </Routes>
      </ThemeProvider>
    </StripeContext.Provider>
  ) : (
    <StripeContext.Provider value={stripePromise}>
      <ThemeProvider theme={darkMode ? themeDark : theme}>
        <SnackbarProvider>
          <CssBaseline />
          {layout === "dashboard" && (
            <>
              <Sidenav
                color={sidenavColor}
                brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
                brandName="EquiTrace Dashboard"
                routes={routes}
                onMouseEnter={handleOnMouseEnter}
                onMouseLeave={handleOnMouseLeave}
              />
            </>
          )}
          <Routes>
            {getRoutes(routes)}
            <Route path="/" element={<Navigate to="/horses" />} />
            <Route path="*" element={<Navigate to="/404" />} />
          </Routes>
        </SnackbarProvider>
      </ThemeProvider>
      {/* {showMobileBanner && <MobileBanner />} */}
    </StripeContext.Provider>
  );
}
